import type { BottomTabBarProps } from "expo-router/tabs";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { parentSections } from "./sections";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    bar: {
      flexDirection: "row",
      backgroundColor: t.color.surface,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
      paddingTop: t.space.xs,
    },
    list: { flex: 1, flexDirection: "row" },
    tab: { flex: 1, gap: t.space.xxs, paddingVertical: t.space.xxs },
  }),
);

/**
 * Bottom tabs Home / Shop / Orders / Profile for the mobile chrome (FR-005, FR-044; board MHome):
 * icon and label, the current tab marked selected, 44 px targets, role "tab".
 * Used as the `tabBar` of the `(parent)/(tabs)` navigator.
 */
export function MobileTabBar({ state, navigation, insets }: BottomTabBarProps) {
  const theme = useTheme();
  const styles = useStyles();
  const currentName = state.routes[state.index]?.name;

  return (
    <View
      style={[
        styles.bar,
        { paddingBottom: Math.max(insets.bottom, theme.space.xs) },
      ]}
      role="navigation"
      aria-label="Sections"
      testID="mobile-tab-bar"
    >
      <View role="tablist" style={styles.list}>
        {parentSections.map((section) => {
          const route = state.routes.find((r) => r.name === section.name);
          const selected = section.name === currentName;
          const color = selected
            ? theme.color.primaryText
            : theme.color.textMuted;
          const onPress = () => {
            if (!route) return;
            const event = navigation.emit({
              type: "tabPress",
              target: route.key,
              canPreventDefault: true,
            });
            if (!selected && !event.defaultPrevented)
              navigation.navigate(route.name);
          };
          return (
            <PressableTarget
              key={section.name}
              accessibilityRole="tab"
              accessibilityLabel={section.label}
              aria-selected={selected}
              onPress={onPress}
              style={styles.tab}
            >
              <section.Icon
                color={color}
                size={theme.size.icon.md}
                testID={`tab-icon-${section.label}`}
              />
              <Text size="xs" weight="semiBold" style={{ color }}>
                {section.label}
              </Text>
            </PressableTarget>
          );
        })}
      </View>
    </View>
  );
}
